import { IonButton, IonPage, IonToast, useIonToast, useIonViewWillEnter } from '@ionic/react';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootState } from '../App';
import { EmailCard } from '../components/EmailCard';
import { Input } from '../components/Forms/Input';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { SiteLogoBanner } from '../components/SiteLogoBanner';
import { useLoading } from '../hooks/useQuirkLoading';
import {
	LoginState,
	resetPassword,
	setEmail,
	setPasswordResetErrorMessage,
	setPasswordResetStatus
} from '../state/loginSlice';
// eslint-disable-next-line css-modules/no-unused-class
import onboardingStyles from '../styles/onboarding.module.css';
import { removeAnonEmail } from '../util/removeAnonEmail';
import styles from './ForgotPassword.module.css';

export const ForgotPassword: React.FC = () => {
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
	const inputRef = useRef<any>(null);
	const [toast] = useIonToast();
	const history = useHistory();
	useLoading({ message: 'Sending email...', isLoading: state.passwordResetStatus === 'resetting' });
	const resetAction = () => {
		const email = removeAnonEmail(state.user.email);
		if (email && email?.length > 3) {
			dispatch(resetPassword({ email }));
		} else {
			toast({ message: 'Please provide a valid email', duration: 1500, position: 'top', color: 'danger' });
		}
	};

	useIonViewWillEnter(() => {
		dispatch(setEmail(''));
		dispatch(setPasswordResetStatus(''));
	});

	return (
		<IonPage>
			<QuirkIonContent flush>
				<div className="login-container">
					<SiteLogoBanner siteName={true} />
					<div className="space20" />
					{state.passwordResetStatus !== 'reset' && (
						<form>
							<div className={onboardingStyles.contentWrapper}>
								<div className={styles.formContainer}>
									<Input
										type="email"
										inputRef={inputRef}
										placeholder="Email"
										tabIndex={1}
										value={state.user.email.startsWith('anon') ? '' : state.user.email}
										onChange={event => {
											dispatch(setEmail(event.target.value));
										}}
										testId="forgotPasswordEmailInput"
										autoComplete="email"
									/>
									<IonButton
										tabIndex={2}
										size="large"
										expand="block"
										onClick={resetAction}
										data-testid="forgotPasswordResetPasswordButton"
									>
										Reset Password
									</IonButton>

									<IonToast
										isOpen={state.passwordResetErrorMessage !== ''}
										duration={1500}
										message={state.passwordResetErrorMessage}
										color="danger"
										position="top"
										onDidDismiss={() => dispatch(setPasswordResetErrorMessage(''))}
									/>
								</div>
							</div>
						</form>
					)}
					{state.passwordResetStatus === 'reset' && (
						<EmailCard
							header={`An email has been sent to ${state.user.email}`}
							message="Please click the link in the email to reset your password. This link will expire in 24 hours."
							testId="forgotPasswordResetSentCard"
						/>
					)}
					<hr />
					<div className={onboardingStyles.panelRow}>
						<IonButton
							color="primary"
							fill="clear"
							size="default"
							tabIndex={3}
							className="radius8"
							onClick={() => history.push('/main/settings/login')}
							expand="block"
						>
							Go back to sign in
						</IonButton>
					</div>
				</div>
			</QuirkIonContent>
		</IonPage>
	);
};
